// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Button, Col, Row, Icons } from "@components";
import type { StepProps } from "@components";
import { AlertContentForm } from "@modules/service/components";
import type { Alert } from "@modules/service/reducer/alerts/types";
import type { AlertEmailAttachment } from "@modules/service/reducer/alerts/types";

type StepData = $Exact<{
  emailContent: Object,
  attachments: Array<AlertEmailAttachment>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepContent extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv().compile(this.schema.emailContent);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.schema && prevProps.schema !== this.schema) {
      this.validate = new Ajv().compile(this.schema.emailContent);
    }
  }

  get data(): StepData {
    const _data = this.props.draft?.content;

    return {
      emailContent: (_data || {}).emailContent || {
        instructionText: null,
        includeAbsenceId: false,
        includeDivisionName: false,
        includeEmployeeRef: false,
        includeJobTitle: false,
        includeContractHours: false,
        includeAbsenceHistory: false,
        includeBradfordScore: false,
        includePhoneNumber: false,
        includeEmployeeStartDate: false,
        includeAbsenceReason: false,
        includePosition: false,
      },
      attachments: (_data || {}).attachments,
    };
  }

  get schema() {
    return {
      emailContent: ((this.props.schema || {}).properties || {}).emailContent,
    };
  }

  isValid = (data: StepData) => {
    if (!this.validate) {
      return false;
    }

    const isValid = this.validate(data.emailContent);
    if (!isValid) {
      console.warn("isValid errors", this.validate.errors);
    }

    return isValid;
  };

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit({}, true);
    }
  };

  updateEmailContents = (formData: Object) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        emailContent: formData,
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12">
            {this.schema && this.data ? (
              <AlertContentForm
                data={this.data.emailContent}
                schema={this.schema.emailContent}
                onChange={this.updateEmailContents}
              />
            ) : null}
          </Col>
        </Row>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm={12} className="mb-3 d-flex justify-content-between">
            <h6>{this.props.t("ATTACHMENTS")}</h6>
          </Col>
          <Col sm={12} className="d-flex justify-content-start">
            <Icons.QuestionCircleFilledIcon
              color="chatbubble-background"
              className="fill no-stroke ml-2 question-circle-icon medium mr-2"
            />
            {this.props.t("ALERT_DETAILS_CONTENT_UPLOAD_EMAIL")}
          </Col>
        </Row>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button
              materialIcon="fast_forward"
              onClick={this.handleSubmit}
              disabled={!this.isValid(this.data)}
            >
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepContent);
